import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import { GeopolTransformerStore } from "../geopol-transformer-store";

interface ColumnTransformerModalProps {
  store: GeopolTransformerStore;
}

export const ColumnTransformerModal = observer(
  ({ store }: ColumnTransformerModalProps) => {
    const [jsonText, setJsonText] = useState<string>("");
    const [jsonError, setJsonError] = useState<string>("");
    const [isSaving, setIsSaving] = useState<boolean>(false);

    // Initialize JSON text from store when modal opens
    // Also refresh config to ensure we have the latest data
    useEffect(() => {
      if (store.showColumnTransformerModal) {
        // Refresh transformer config to get latest column transformers
        store.fetchTransformerConfig().then(() => {
          try {
            const columnTransformers = store.transformerConfig.column_transformers || [];
            const formattedJson = JSON.stringify(columnTransformers, null, 2);
            setJsonText(formattedJson);
            setJsonError("");
          } catch (error) {
            setJsonText("[]");
            setJsonError("");
          }
        }).catch(() => {
          // If fetch fails, still try to show what's in store
          try {
            const columnTransformers = store.transformerConfig.column_transformers || [];
            const formattedJson = JSON.stringify(columnTransformers, null, 2);
            setJsonText(formattedJson);
            setJsonError("");
          } catch (error) {
            setJsonText("[]");
            setJsonError("");
          }
        });
      }
    }, [store.showColumnTransformerModal]);

    const handleClose = () => {
      if (!isSaving) {
        setJsonText("");
        setJsonError("");
        store.setShowColumnTransformerModal(false);
      }
    };

    const validateJson = (text: string): { valid: boolean; error?: string; data?: any } => {
      if (!text.trim()) {
        return { valid: true, data: [] }; // Empty is valid, treat as empty array
      }
      
      try {
        const parsed = JSON.parse(text);
        // Ensure it's an array or object
        if (typeof parsed !== 'object') {
          return { valid: false, error: "JSON must be an object or array" };
        }
        return { valid: true, data: parsed };
      } catch (error: any) {
        return { valid: false, error: error.message || "Invalid JSON format" };
      }
    };

    const handleJsonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newText = event.target.value;
      setJsonText(newText);
      
      // Validate JSON in real-time
      const validation = validateJson(newText);
      if (validation.valid) {
        setJsonError("");
      } else {
        setJsonError(validation.error || "Invalid JSON");
      }
    };

    const handleSave = async () => {
      const validation = validateJson(jsonText);
      
      if (!validation.valid) {
        setJsonError(validation.error || "Invalid JSON format");
        return;
      }

      setIsSaving(true);
      try {
        await store.saveColumnTransformers(validation.data);
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <Dialog
        open={store.showColumnTransformerModal}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "500px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Column Transformers Editor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Edit the column transformer configuration as JSON
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={20}
              value={jsonText}
              onChange={handleJsonChange}
              error={!!jsonError}
              helperText={jsonError || "Enter valid JSON (object or array)"}
              variant="outlined"
              placeholder='[]'
              sx={{
                "& .MuiInputBase-root": {
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaving || !!jsonError}
            sx={{
              backgroundColor: "#3A80F4",
              "&:hover": {
                backgroundColor: "#2563EB",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

